<app-main-layout>
  <div class="bg-gray-50 min-h-screen">
  <div class="py-6 px-4 sm:px-6 max-w-[1500px] mx-auto" [class.pb-28]="countMode || selected.size">

    <!-- ============ HERO ============ -->
    <div class="relative overflow-hidden rounded-3xl bg-neutral-900 text-white px-6 sm:px-8 py-7 mb-6 shadow-lg">
      <div class="pointer-events-none absolute -right-16 -top-16 h-64 w-64 rounded-full bg-amber-400/20 blur-3xl"></div>
      <div class="pointer-events-none absolute right-10 bottom-0 opacity-[0.07]"><mat-icon class="!text-[160px] !h-[160px] !w-[160px]">warehouse</mat-icon></div>

      <div class="relative flex flex-col xl:flex-row xl:items-end justify-between gap-6">
        <div class="max-w-xl">
          <div class="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-[0.15em] text-amber-400">
            <mat-icon class="!text-[14px] !h-3.5 !w-3.5">inventory_2</mat-icon> Products
          </div>
          <h1 class="mt-2 text-3xl font-bold tracking-tight">Inventory</h1>
          <p class="mt-2 text-sm text-white/60 leading-relaxed">What's in each yard and what it sells for{{ canEdit ? ' - click an item to edit it, or count stock straight into the list.' : '.' }}</p>
        </div>

        <!-- Actions -->
        <div class="flex flex-wrap items-center gap-2">
          @if (canEdit) {
            <button type="button" class="flex items-center gap-2 pl-2 pr-4 py-2 rounded-2xl font-bold text-sm transition-all hover:-translate-y-0.5"
              [ngClass]="countMode ? 'bg-white text-neutral-900' : 'bg-amber-400 text-neutral-900 hover:bg-amber-300 shadow-lg shadow-amber-500/20'"
              (click)="toggleCountMode()" [attr.aria-pressed]="countMode" matTooltip="Type the counted stock straight into the list, then save everything together">
              <span class="h-8 w-8 rounded-xl bg-neutral-900 text-amber-400 flex items-center justify-center"><mat-icon class="!text-[18px] !h-[18px] !w-[18px]">fact_check</mat-icon></span>
              {{ countMode ? 'Counting…' : 'Stock count' }}
            </button>
            <input #importInput type="file" class="hidden" accept=".xlsx,.xls,.csv" (change)="onImportFile($event)" />
            <button type="button" class="flex items-center gap-2 px-4 py-2.5 rounded-2xl bg-white/[0.08] border border-white/10 text-sm font-semibold hover:bg-white/[0.14] transition-colors disabled:opacity-50"
              [disabled]="importing || savingAll" (click)="importInput.click()" matTooltip="Upload an edited Export sheet to update stock and prices - you'll see the changes before anything is saved">
              @if (importing) { <span class="inline-block h-4 w-4 rounded-full border-2 border-white border-t-transparent animate-spin"></span> }
              @else { <mat-icon class="!text-[18px] !h-[18px] !w-[18px] text-sky-300">upload</mat-icon> }
              {{ importing ? 'Reading…' : 'Import' }}
            </button>
          }
          <button type="button" class="flex items-center gap-2 px-4 py-2.5 rounded-2xl bg-white/[0.08] border border-white/10 text-sm font-semibold hover:bg-white/[0.14] transition-colors disabled:opacity-50"
            [disabled]="exporting || loading || !count" (click)="exportToExcel()" matTooltip="Every item matching the current filters - tick items in the list to export just those">
            @if (exporting) { <span class="inline-block h-4 w-4 rounded-full border-2 border-white border-t-transparent animate-spin"></span> }
            @else { <mat-icon class="!text-[18px] !h-[18px] !w-[18px] text-emerald-300">download</mat-icon> }
            {{ exporting ? 'Exporting…' : 'Export' }} <span class="text-white/40 font-medium tabular-nums">{{ variantCount | number }}</span>
          </button>
          <button type="button" class="flex items-center gap-2 px-4 py-2.5 rounded-2xl bg-white/[0.08] border border-white/10 text-sm font-semibold hover:bg-white/[0.14] transition-colors disabled:opacity-40"
            [disabled]="!selected.size" (click)="printLabels()"
            [matTooltip]="selected.size ? 'Barcode labels for the ' + selected.size + ' ticked item' + (selected.size === 1 ? '' : 's') : 'Tick items in the list to print their barcode labels'">
            <mat-icon class="!text-[18px] !h-[18px] !w-[18px] text-amber-300">qr_code_2</mat-icon>Barcodes
            @if (selected.size) { <span class="text-amber-300 tabular-nums">{{ selected.size }}</span> }
          </button>
        </div>
      </div>

      <!-- Figures - running low / out of stock are shortcuts -->
      <div class="relative mt-7 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
        <div class="rounded-2xl bg-white/[0.06] border border-white/10 px-4 py-3">
          <div class="flex items-center gap-1.5 text-[11px] font-semibold text-white/50 uppercase tracking-wide"><mat-icon class="!text-[14px] !h-3.5 !w-3.5">inventory_2</mat-icon>Items{{ filtersActive ? ' matching' : '' }}</div>
          <div class="mt-1 text-2xl font-bold inv-num">{{ loading && !rows.length ? '–' : (variantCount | number) }}</div>
          <div class="text-[11px] text-white/40">{{ groupByProduct ? (count | number) + ' products' : 'Product variants' }}</div>
        </div>
        <div class="rounded-2xl bg-white/[0.06] border border-white/10 px-4 py-3">
          <div class="flex items-center gap-1.5 text-[11px] font-semibold text-white/50 uppercase tracking-wide"><mat-icon class="!text-[14px] !h-3.5 !w-3.5">stacks</mat-icon>Units on hand</div>
          <div class="mt-1 text-2xl font-bold inv-num truncate">{{ loading && !rows.length ? '–' : (unitsOnHand | number) }}</div>
          <div class="text-[11px] text-white/40">Across {{ store === 'ALL' ? 'both yards' : store }}</div>
        </div>
        <div class="rounded-2xl bg-white/[0.06] border border-white/10 px-4 py-3" matTooltip="Units on hand × regular price. Made-to-order items are not counted.">
          <div class="flex items-center gap-1.5 text-[11px] font-semibold text-white/50 uppercase tracking-wide"><mat-icon class="!text-[14px] !h-3.5 !w-3.5">payments</mat-icon>Stock value</div>
          <div class="mt-1 text-2xl font-bold inv-num truncate">{{ loading && !rows.length ? '–' : ('£' + (stockValue | number: '1.0-0')) }}</div>
          <div class="text-[11px] text-white/40">At selling price</div>
        </div>
        <button type="button" class="text-left rounded-2xl border px-4 py-3 transition-colors" (click)="setStatus('LOW STOCK')"
          [ngClass]="status === 'LOW STOCK' ? 'bg-amber-400/20 border-amber-400/60' : 'bg-white/[0.06] border-white/10 hover:bg-white/[0.1]'">
          <div class="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-amber-300"><mat-icon class="!text-[14px] !h-3.5 !w-3.5">trending_down</mat-icon>Running low</div>
          <div class="mt-1 text-2xl font-bold inv-num">{{ loading && !rows.length ? '–' : lowStockCount }}</div>
          <div class="text-[11px] text-white/40 flex items-center gap-0.5">Under {{ lowStockThreshold }} left <mat-icon class="!text-[12px] !h-3 !w-3">arrow_forward</mat-icon></div>
        </button>
        <button type="button" class="text-left rounded-2xl border px-4 py-3 transition-colors" (click)="setStatus('OUT OF STOCK')"
          [ngClass]="status === 'OUT OF STOCK' ? 'bg-rose-400/20 border-rose-400/60' : 'bg-white/[0.06] border-white/10 hover:bg-white/[0.1]'">
          <div class="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-rose-300"><mat-icon class="!text-[14px] !h-3.5 !w-3.5">production_quantity_limits</mat-icon>Out of stock</div>
          <div class="mt-1 text-2xl font-bold inv-num">{{ loading && !rows.length ? '–' : outOfStockCount }}</div>
          <div class="text-[11px] text-white/40 flex items-center gap-0.5">Nothing to sell <mat-icon class="!text-[12px] !h-3 !w-3">arrow_forward</mat-icon></div>
        </button>
      </div>
    </div>

    @if (loadError) {
      <div class="mb-5 bg-red-50 border border-red-200 text-red-700 rounded-2xl px-4 py-3 text-sm flex items-center justify-between gap-3">
        <span class="flex items-center gap-2"><mat-icon class="!text-[18px] !h-[18px] !w-[18px]">cloud_off</mat-icon>Couldn't load inventory from the server.</span>
        <button class="text-xs font-semibold px-3 py-1.5 rounded-lg bg-white border border-red-200 hover:bg-red-100" (click)="loadInventory()">Try again</button>
      </div>
    }

    <!-- ============ TOOLBAR ============ -->
    <div class="sticky top-0 z-20 -mx-4 sm:mx-0 px-4 sm:px-0 py-3 mb-3 bg-gray-50/90 backdrop-blur">
      <div class="flex flex-col lg:flex-row lg:items-center gap-2.5">
        <div class="relative flex-1 min-w-0">
          <mat-icon class="absolute left-3.5 top-1/2 -translate-y-1/2 !text-gray-400 !text-[20px] !h-5 !w-5 pointer-events-none">search</mat-icon>
          <input type="text" placeholder="Search by product name, SKU or barcode…"
            class="w-full pl-11 pr-10 py-2.5 rounded-xl border border-gray-200 bg-white shadow-sm focus:border-amber-400 focus:ring-4 focus:ring-amber-100 outline-none transition-all text-sm"
            name="inventory-search" [(ngModel)]="search" (ngModelChange)="onSearchInput()" />
          @if (search) {
          <button class="absolute right-2.5 top-1/2 -translate-y-1/2 h-7 w-7 rounded-full text-gray-400 hover:bg-gray-100 hover:text-gray-700 flex items-center justify-center" (click)="search = ''; applyFilters()" aria-label="Clear search">
            <mat-icon class="!text-[18px] !h-[18px] !w-[18px]">close</mat-icon>
          </button>
          }
        </div>
        <div class="flex flex-wrap items-center gap-2">
          <select class="inv-select !bg-white shadow-sm !py-2.5 lg:max-w-[200px]" [(ngModel)]="category" (ngModelChange)="applyFilters()" aria-label="Category">
            @for (opt of categoryOptions; track opt) { <option [value]="opt">{{ opt === 'ALL' ? 'All categories' : opt }}</option> }
          </select>
          <select class="inv-select !bg-white shadow-sm !py-2.5" [(ngModel)]="sort" (ngModelChange)="applyFilters()" aria-label="Sort">
            @for (o of sortOptions; track o.value) { <option [value]="o.value">{{ o.label }}</option> }
          </select>
          <div class="flex items-center gap-1 bg-white border border-gray-200 rounded-xl p-1 shadow-sm" role="group" aria-label="Yard">
            @for (s of storeOptions; track s.value) {
            <button class="px-3 py-1.5 rounded-lg text-xs font-semibold transition-all whitespace-nowrap" [ngClass]="store === s.value ? 'bg-neutral-900 text-white shadow-sm' : 'text-gray-500 hover:text-gray-900 hover:bg-gray-50'" (click)="setStore(s.value)">{{ s.label }}</button>
            }
          </div>
          <button class="h-10 flex items-center gap-1.5 px-3 rounded-xl border text-xs font-semibold shadow-sm transition-all whitespace-nowrap"
            [ngClass]="groupByProduct ? 'bg-neutral-900 text-white border-neutral-900' : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300'"
            (click)="toggleGrouping()" [attr.aria-pressed]="groupByProduct"
            [matTooltip]="groupByProduct ? 'Showing each product once with its variants under it - click for the plain list' : 'Put the variants of each product together under its name'">
            <mat-icon class="!text-[16px] !h-4 !w-4" [ngClass]="groupByProduct ? '!text-amber-400' : ''">account_tree</mat-icon>Group
          </button>
          <button type="button" class="h-10 w-10 rounded-xl border border-gray-200 bg-white text-gray-400 shadow-sm hover:text-gray-800 flex items-center justify-center disabled:opacity-50"
            [disabled]="loading" (click)="loadInventory()" matTooltip="Refresh" aria-label="Refresh">
            <mat-icon class="!text-[18px] !h-[18px] !w-[18px]" [class.animate-spin]="loading">refresh</mat-icon>
          </button>
        </div>
      </div>

      <div class="mt-2.5 flex flex-wrap items-center gap-2">
        <div class="flex flex-wrap bg-white border border-gray-200 rounded-xl p-1 gap-1 shadow-sm">
          @for (o of statusOptions; track o.value) {
          <button class="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-semibold transition-all whitespace-nowrap"
            [ngClass]="status === o.value ? 'bg-neutral-900 text-white shadow-sm' : 'text-gray-500 hover:text-gray-900 hover:bg-gray-50'"
            (click)="o.value === 'ALL' ? (status !== 'ALL' && setStatus(status)) : setStatus(o.value)">
            @if (o.key !== 'all') { <span class="h-1.5 w-1.5 rounded-full" [ngClass]="statusDot(o.key)"></span> }
            {{ o.value === 'ALL' ? 'All' : o.label }}
            <span class="inv-num" [ngClass]="status === o.value ? 'text-amber-400' : 'text-gray-400'">{{ statusCounts[o.key] | number }}</span>
          </button>
          }
        </div>
        <button class="flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-semibold border shadow-sm transition-all"
          [ngClass]="onSale ? 'bg-rose-500 text-white border-rose-500 shadow-rose-200' : 'bg-white text-gray-600 border-gray-200 hover:border-rose-300 hover:text-rose-700'" (click)="toggleOnSale()" [attr.aria-pressed]="onSale">
          <mat-icon class="!text-[14px] !h-[14px] !w-[14px]">sell</mat-icon> On sale
        </button>
        @if (filtersActive) {
          <button class="flex items-center gap-1 text-xs font-semibold text-amber-700 hover:underline px-1" (click)="onResetFilters()">
            <mat-icon class="!text-[14px] !h-3.5 !w-3.5">filter_alt_off</mat-icon>Clear all
          </button>
        }
        <span class="hidden xl:flex ml-auto items-center gap-1 text-[11px] text-gray-400">
          <mat-icon class="!text-[13px] !h-[13px] !w-[13px]">info</mat-icon>
          Status counts {{ store === 'ALL' ? 'both yards together' : 'the ' + store + ' yard' }}
        </span>
      </div>
    </div>

    @if (countMode) {
      <div class="mb-4 flex items-center gap-3 rounded-2xl bg-amber-50 border border-amber-200 px-4 py-3 text-sm text-amber-900">
        <span class="h-9 w-9 rounded-xl bg-amber-400 text-neutral-900 flex items-center justify-center flex-shrink-0"><mat-icon class="!text-[18px] !h-[18px] !w-[18px]">fact_check</mat-icon></span>
        <div><b>Stock count is on.</b> Type what you counted into Markstey - changed rows are highlighted, then <b>Save</b> at the bottom. Prices are edited per item with the pencil.</div>
      </div>
    }

    <!-- ============ LIST ============ -->
    @if (loading && rows.length === 0) {
      <div class="flex flex-col gap-2.5">
        @for (r of [1, 2, 3, 4, 5, 6]; track r) {
        <div class="flex items-center gap-4 rounded-2xl bg-white border border-gray-100 px-5 py-4 animate-pulse">
          <div class="h-[18px] w-[18px] rounded-md bg-gray-100"></div>
          <div class="h-12 w-12 rounded-xl bg-gray-100"></div>
          <div class="flex-1"><div class="h-3.5 w-64 max-w-full bg-gray-200 rounded"></div><div class="h-2.5 w-40 bg-gray-100 rounded mt-2"></div></div>
          <div class="hidden lg:block h-3.5 w-12 bg-gray-200 rounded"></div>
          <div class="hidden lg:block h-5 w-20 bg-gray-100 rounded-full"></div>
          <div class="hidden lg:block h-3.5 w-16 bg-gray-200 rounded"></div>
        </div>
        }
      </div>
    } @else if (rows.length === 0) {
      <div class="rounded-3xl border-2 border-dashed border-gray-200 bg-white py-16 text-center">
        <span class="mx-auto h-14 w-14 rounded-2xl bg-amber-50 text-amber-600 flex items-center justify-center"><mat-icon class="!text-[28px] !h-7 !w-7">{{ filtersActive ? 'search_off' : 'inventory_2' }}</mat-icon></span>
        <p class="mt-3 text-sm font-semibold text-gray-700">No items found</p>
        <p class="text-xs text-gray-400">Try a different search, or clear the filters</p>
        @if (filtersActive) { <button class="mt-4 text-xs font-semibold text-amber-700 hover:underline" (click)="onResetFilters()">Clear filters</button> }
      </div>
    } @else {

      <!-- Column headings -->
      <div class="hidden lg:grid inv-row px-5 pb-2 text-[11px] font-semibold text-gray-400 uppercase tracking-wide" [class.cost]="canSeeCost">
        <button type="button" class="inv-check" [class.on]="tickState(rows) === 'all'" [class.mixed]="tickState(rows) === 'some'" (click)="toggleRows(rows)"
          aria-label="Tick every item on this page" matTooltip="Tick every item on this page"><mat-icon>{{ tickState(rows) === 'all' ? 'check' : 'remove' }}</mat-icon></button>
        <span>Item</span>
        <span class="text-center">Markstey</span>
        <span class="text-center">Total</span>
        <span>Status</span>
        <span class="text-right">Price</span>
        @if (canSeeCost) { <span class="text-right" matTooltip="What we pay the supplier">Cost</span> }
        <span class="text-right" matTooltip="Approximate m² covered per pack or unit">Coverage</span>
        <span></span>
      </div>

      <div class="flex flex-col gap-2.5 transition-opacity" [class.opacity-60]="loading">
        @for (group of groups; track group.rows[0].productVariantId) {
        <div class="rounded-2xl bg-white border border-gray-100 overflow-hidden transition-all duration-200 hover:border-gray-200 hover:shadow-[0_8px_24px_-12px_rgba(0,0,0,0.15)]">

          <!-- A product with several variants: its name once, its variants beneath -->
          @if (group.rows.length > 1) {
          <div class="flex items-center gap-3 px-5 py-3 bg-gradient-to-r from-stone-50 to-white border-b border-gray-100">
            <button type="button" class="inv-check flex-shrink-0" [class.on]="tickState(group.rows) === 'all'" [class.mixed]="tickState(group.rows) === 'some'" (click)="toggleRows(group.rows)"
              [attr.aria-label]="'Tick all variants of ' + group.name"><mat-icon>{{ tickState(group.rows) === 'all' ? 'check' : 'remove' }}</mat-icon></button>
            @if (group.image) {
              <img [src]="group.image.includes(',') ? group.image.split(',')[0] : group.image" [alt]="group.name" loading="lazy" class="h-11 w-11 object-cover rounded-xl ring-1 ring-black/5 shadow-sm flex-shrink-0" />
            } @else {
              <span class="h-11 w-11 rounded-xl border border-dashed border-gray-300 flex items-center justify-center text-gray-300 flex-shrink-0"><mat-icon class="!text-[18px] !h-[18px] !w-[18px]">image</mat-icon></span>
            }
            <div class="min-w-0 flex flex-wrap items-center gap-2">
              <span class="font-bold text-gray-900 truncate">{{ group.name }}</span>
              <span class="text-[11px] font-semibold px-2 py-0.5 rounded-md bg-neutral-900 text-amber-400">{{ group.rows.length }} variants</span>
              @if (group.category) { <span class="text-[11px] px-2 py-0.5 rounded-md bg-white border border-gray-200 text-gray-500">{{ group.category }}</span> }
            </div>
          </div>
          }

          @for (row of group.rows; track row.productVariantId; let last = $last) {
          <div class="inv-row px-4 sm:px-5 py-3 transition-colors" [class.cost]="canSeeCost" [class.border-b]="!last" [class.border-gray-100]="!last"
            [ngClass]="row.saveError ? 'bg-rose-50/60' : isChanged(row) ? 'bg-amber-50/70' : isSelected(row) ? 'bg-amber-50/50' : 'hover:bg-amber-50/30'">

            <!-- Tick -->
            <button type="button" class="inv-check" [class.on]="isSelected(row)" (click)="toggleRow(row)"
              [attr.aria-label]="'Tick ' + row.productName + (row.label ? ' - ' + row.label : '')"><mat-icon>{{ isSelected(row) ? 'check' : 'remove' }}</mat-icon></button>

            <!-- Item -->
            <div class="flex items-center gap-3 min-w-0" [class.lg:pl-6]="group.rows.length > 1">
              @if (group.rows.length === 1) {
                @if (row.image) {
                  <img [src]="row.image.includes(',') ? row.image.split(',')[0] : row.image" [alt]="row.productName" loading="lazy" class="h-12 w-12 object-cover rounded-xl ring-1 ring-black/5 shadow-sm flex-shrink-0" />
                } @else {
                  <span class="h-12 w-12 rounded-xl bg-gray-50 ring-1 ring-black/5 flex items-center justify-center text-gray-300 flex-shrink-0"><mat-icon class="!text-[18px] !h-[18px] !w-[18px]">image</mat-icon></span>
                }
              } @else {
                <span class="h-8 w-1 rounded-full bg-amber-300 flex-shrink-0"></span>
              }
              <div class="min-w-0">
                @if (group.rows.length === 1) {
                  <div class="flex items-center flex-wrap gap-x-2 gap-y-0.5">
                    <button class="font-semibold text-gray-900 hover:text-amber-700 text-left leading-snug line-clamp-2" [disabled]="!canEdit" (click)="openEdit(row)">{{ row.productName }}</button>
                    @if (row.label) { <span class="text-[11px] font-semibold px-2 py-0.5 rounded-md bg-amber-100 text-amber-800 whitespace-nowrap">{{ row.label }}</span> }
                  </div>
                } @else {
                  <button class="font-semibold text-gray-900 hover:text-amber-700 text-left leading-snug" [disabled]="!canEdit" (click)="openEdit(row)">{{ row.label || row.sku || 'Variant' }}</button>
                }
                <div class="mt-0.5 flex items-center flex-wrap gap-x-2.5 gap-y-0.5 text-xs text-gray-400">
                  @if (row.barcode) {
                    <button class="inline-flex items-center gap-1 font-mono text-gray-500 hover:text-amber-700" (click)="copyBarcode(row)" matTooltip="Copy barcode">
                      <mat-icon class="!text-[13px] !h-[13px] !w-[13px]">barcode</mat-icon>{{ row.barcode }}
                    </button>
                  } @else {
                    <span class="text-gray-300 inline-flex items-center gap-1"><mat-icon class="!text-[13px] !h-[13px] !w-[13px]">barcode</mat-icon>no barcode</span>
                  }
                  @if (row.sku) {
                    <button class="inline-flex items-center gap-1 font-mono text-[11px] text-gray-600 bg-gray-100 hover:bg-amber-100 hover:text-amber-800 rounded-md px-1.5 py-0.5 transition-colors max-w-full truncate" (click)="copySku(row)" matTooltip="Copy SKU">
                      <mat-icon class="!text-[13px] !h-[13px] !w-[13px] flex-shrink-0">tag</mat-icon>{{ row.sku }}
                    </button>
                  } @else {
                    <span class="font-mono">No SKU</span>
                  }
                  @if (group.rows.length === 1 && row.category) { <span class="px-2 py-0.5 rounded-md bg-gray-100 text-gray-500">{{ row.category }}</span> }
                </div>
                @if (row.saveError) { <div class="mt-1 text-xs text-rose-600">{{ row.saveError }}</div> }
              </div>
            </div>

            <!-- Actions -->
            <div class="inv-actions flex items-center justify-end gap-0.5">
              @if (canEdit) {
                <button class="h-8 w-8 rounded-lg text-gray-500 hover:bg-amber-50 hover:text-amber-700 flex items-center justify-center transition-colors" (click)="openEdit(row)" matTooltip="Edit stock and price" aria-label="Edit stock and price"><mat-icon class="!text-[18px] !h-[18px] !w-[18px]">edit</mat-icon></button>
              }
              <button class="h-8 w-8 rounded-lg text-gray-500 hover:bg-sky-50 hover:text-sky-700 flex items-center justify-center transition-colors" (click)="openHistory(row)" matTooltip="Stock history" aria-label="Stock history"><mat-icon class="!text-[18px] !h-[18px] !w-[18px]">history</mat-icon></button>
            </div>

            <!-- Figures -->
            <div class="inv-metrics">
              <!-- Markstey -->
              <div class="lg:text-center">
                <span class="inv-mlabel">Markstey</span>
                @if (countMode) {
                  <div class="inline-flex flex-col items-center">
                    <input type="number" min="0" step="1" [attr.aria-label]="'markstey stock for ' + row.productName"
                      class="w-20 px-2 py-1.5 rounded-lg border bg-white text-center font-semibold outline-none focus:ring-4 focus:ring-amber-100 focus:border-amber-400 transition-all"
                      [ngClass]="qtyOf(row, 'markstey') !== savedQtyOf(row, 'markstey') ? 'border-amber-400 ring-2 ring-amber-100' : 'border-gray-200'"
                      [ngModel]="qtyOf(row, 'markstey')" (ngModelChange)="setQty(row, 'markstey', $event)" />
                    @if (qtyOf(row, 'markstey') !== savedQtyOf(row, 'markstey')) { <span class="text-[10px] text-gray-400 mt-0.5">was {{ savedQtyOf(row, 'markstey') }}</span> }
                  </div>
                } @else {
                  <span class="font-semibold inv-num" [ngClass]="isUnlimited(qtyOf(row, 'markstey')) ? 'text-sky-600 text-xs' : qtyOf(row, 'markstey') <= 0 ? 'text-gray-300' : 'text-gray-800'">{{ isUnlimited(qtyOf(row, 'markstey')) ? 'Made to order' : (qtyOf(row, 'markstey') | number) }}</span>
                }
              </div>

              <!-- Total -->
              <div class="lg:text-center">
                <span class="inv-mlabel">Total</span>
                <span class="font-bold text-gray-900 inv-num">{{ isUnlimited(rowTotal(row)) ? '–' : (rowTotal(row) | number) }}</span>
                @if (!isUnlimited(rowTotal(row))) {
                  <span class="inv-level lg:mx-auto mt-1"><span [class]="statusDot(rowStatus(row))" [style.width.%]="stockLevel(row)"></span></span>
                }
              </div>

              <!-- Status -->
              <div>
                <span class="inv-mlabel">Status</span>
                <span class="inline-flex items-center gap-1.5 text-[11px] font-semibold px-2.5 py-1 rounded-lg whitespace-nowrap ring-1 ring-inset ring-black/5" [class]="statusClass(rowStatus(row))">
                  <span class="h-1.5 w-1.5 rounded-full" [class]="statusDot(rowStatus(row))"></span>{{ statusLabel(rowStatus(row)) }}
                </span>
              </div>

              <!-- Price -->
              <div class="lg:text-right whitespace-nowrap">
                <span class="inv-mlabel">Price</span>
                @if (row.discountedPrice) {
                  <div class="font-bold text-rose-600 inv-num">£{{ row.discountedPrice | number: '1.2-2' }}</div>
                  <div class="text-[11px] text-gray-400 line-through inv-num">£{{ row.regularPrice | number: '1.2-2' }}</div>
                } @else {
                  <div class="font-semibold text-gray-900 inv-num">£{{ row.regularPrice | number: '1.2-2' }}</div>
                }
                @if (row.priceMS) { <div class="text-[11px] text-gray-400 inv-num">£{{ row.priceMS | number: '1.2-2' }}/m²</div> }
              </div>

              <!-- Cost (super admins and the purchasing team only) -->
              @if (canSeeCost) {
              <div class="lg:text-right whitespace-nowrap">
                <span class="inv-mlabel">Cost</span>
                @if (row.costPrice !== null && row.costPrice !== undefined) {
                  <div class="text-gray-700 inv-num">£{{ row.costPrice | number: '1.2-2' }}</div>
                  @if (margin(row) !== null) { <div class="text-[11px] font-semibold" [class]="margin(row)! >= 30 ? 'text-emerald-600' : margin(row)! >= 15 ? 'text-amber-600' : 'text-rose-600'" matTooltip="Price minus cost, as a share of the price (VAT not taken off)">{{ margin(row) }}% margin</div> }
                } @else {
                  <span class="text-gray-300" matTooltip="Not recorded yet">–</span>
                }
              </div>
              }

              <!-- Coverage -->
              <div class="lg:text-right whitespace-nowrap">
                <span class="inv-mlabel">Coverage</span>
                @if (row.approxCoverage) { <span class="text-gray-600 inv-num">{{ row.approxCoverage | number: '1.0-4' }} m²</span> } @else { <span class="text-gray-300">–</span> }
              </div>
            </div>
          </div>
          }
        </div>
        }
      </div>

      <div class="mt-4 rounded-2xl bg-white border border-gray-100 shadow-sm overflow-hidden">
        <mat-paginator [length]="count" [pageSize]="itemsPerPage" [pageIndex]="pageNumber" [pageSizeOptions]="[10, 20, 50, 100]"
          showFirstLastButtons (page)="updateList($event)">
        </mat-paginator>
      </div>
    }

  </div>
  </div>

  <!-- Ticked items: export or print just those (ticks stay across pages / filters) -->
  @if (selected.size) {
  <div class="fixed left-1/2 -translate-x-1/2 z-40 max-w-[calc(100vw-2rem)] transition-all" [ngClass]="countMode ? 'bottom-24' : 'bottom-6'">
    <div class="flex items-center gap-1 rounded-2xl bg-neutral-900/95 backdrop-blur text-white pl-4 pr-2 py-2 shadow-2xl shadow-black/30 ring-1 ring-white/10">
      <span class="flex items-center gap-2 pr-3 mr-1 border-r border-white/10 whitespace-nowrap">
        <span class="h-6 min-w-[24px] px-1.5 rounded-full bg-amber-400 text-neutral-900 text-xs font-bold flex items-center justify-center inv-num">{{ selected.size }}</span>
        <span class="text-sm font-semibold">item{{ selected.size === 1 ? '' : 's' }} ticked</span>
      </span>
      <button class="flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm font-semibold hover:bg-white/10 transition-colors" (click)="exportSelected()">
        <mat-icon class="!text-[18px] !h-[18px] !w-[18px] !text-emerald-400">download</mat-icon><span class="hidden sm:inline">Export</span>
      </button>
      <button class="flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm font-semibold hover:bg-white/10 transition-colors" (click)="printLabels()" matTooltip="Barcode labels, in Odoo's label layout">
        <mat-icon class="!text-[18px] !h-[18px] !w-[18px] !text-amber-400">qr_code_2</mat-icon><span class="hidden sm:inline">Print barcodes</span>
      </button>
      <button class="ml-1 h-8 w-8 rounded-full text-white/60 hover:bg-white/10 hover:text-white flex items-center justify-center transition-colors" (click)="clearSelection()" matTooltip="Untick everything" aria-label="Untick everything">
        <mat-icon class="!text-[18px] !h-[18px] !w-[18px]">close</mat-icon>
      </button>
    </div>
  </div>
  }

  <!-- Stock count: save bar -->
  @if (countMode) {
  <div class="fixed bottom-0 left-0 right-0 z-40 pointer-events-none">
    <div class="max-w-[1700px] mx-auto px-6 pb-5 flex justify-center lg:justify-end">
      <div class="pointer-events-auto bg-neutral-900 text-white rounded-2xl shadow-2xl px-5 py-3 flex items-center gap-4">
        @if (savingAll) {
          <span class="inline-block h-4 w-4 rounded-full border-2 border-amber-400 border-t-transparent animate-spin"></span>
          <span class="text-sm">Saving {{ saveProgress }} of {{ changedRows.length + saveProgress }}…</span>
        } @else {
          <span class="text-sm">
            @if (changedRows.length) { <b class="text-amber-400">{{ changedRows.length }}</b> stock change{{ changedRows.length === 1 ? '' : 's' }} to save }
            @else { No changes yet }
          </span>
          @if (changedRows.length) { <button class="text-xs font-semibold text-neutral-400 hover:text-white" (click)="discardChanges()">Discard</button> }
          <button class="px-5 py-2 rounded-xl bg-gradient-to-b from-amber-400 to-amber-500 text-neutral-900 font-bold text-sm hover:from-amber-300 hover:to-amber-400 active:scale-[0.98] transition-all disabled:opacity-40 disabled:cursor-not-allowed" [disabled]="!changedRows.length" (click)="saveAll()">
            Save {{ changedRows.length || '' }}
          </button>
          <button class="text-xs font-semibold text-neutral-400 hover:text-white" (click)="toggleCountMode()">Finish</button>
        }
      </div>
    </div>
  </div>
  }
</app-main-layout>
